import { Flags } from "@/feature-flags";
import { useOnaEnabledForOrg } from "@/hooks/use-ona-enabled";
import { useGetAccount } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useFeatureFlag, type SettingTypeOf, type SettingValue } from "configcat-react";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export function useFlag<T extends SettingValue>(
    flagKey: string,
    defaultValue: T,
): { value: SettingTypeOf<T>; loading: boolean } {
    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();

    const { value, loading } = useFeatureFlag<T>(
        flagKey,
        defaultValue,
        account
            ? {
                  custom: {
                      organization_id: organization?.id ?? "",
                  },
                  identifier: account.id,
                  email: account.email,
              }
            : undefined,
    );

    return { value, loading };
}

// Helper methods to OR feature flags and useOnaEnabled
// TODO(gpl): Team Agent plans to consolidate these soon
export const useFlagOnaAIEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OnaAIEnabled, false);
    const { value: tierEnabled, loading: tierLoading } = useOnaEnabledForOrg();

    return {
        value: flagEnabled || tierEnabled,
        loading: flagLoading || tierLoading,
    };
};

export const useFlagOnaEnvironmentOnboardingEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OnaEnvironmentOnboardingEnabled, false);
    const { value: tierEnabled, loading: tierLoading } = useOnaEnabledForOrg();

    return {
        value: flagEnabled || tierEnabled,
        loading: flagLoading || tierLoading,
    };
};

export const useFlagIsSentryEnabled = () => {
    return useFlag(Flags.IsSentryEnabled, false);
};

export const useFlagAgentFollowModeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.EnableAgentFollowMode, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export enum WorkflowAccessLevel {
    None = "none",
    Teaser = "teaser",
    Full = "full",
}

export const useFlagWorkflowsEnabled = () => {
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.WorkflowsEnabled, false);

    let accessLevel: WorkflowAccessLevel = WorkflowAccessLevel.None;
    if (flagEnabled === true) {
        accessLevel = WorkflowAccessLevel.Full;
    } else if (organization?.tier === OrganizationTier.FREE_ONA || organization?.tier === OrganizationTier.CORE) {
        accessLevel = WorkflowAccessLevel.Teaser;
    }

    return {
        accessLevel: accessLevel,
        loading: flagLoading || isPendingOrganization,
    };
};

export const useFlagGroupManagementEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.GroupManagementEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagIsMobileVscodeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.IsMobileVscodeEnabled, true);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagMcpEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.McpEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagGitHubAppEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.GitHubAppEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagCustomDomainEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.CustomDomainEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};
export const useFlagPrebuildsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.PrebuildsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};
